import { useState, useCallback, useMemo } from 'react';
import { Chess } from 'chess.js';

/**
 * Custom hook for managing chess game state
 * Single Responsibility: Game state management
 */
export const useGameState = () => {
  const [game, setGame] = useState(() => new Chess());
  const [moveIndex, setMoveIndex] = useState(0);
  const [currentLine, setCurrentLine] = useState(null);
  
  const [mistakes, setMistakes] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);
  const [hintsUsed, setHintsUsed] = useState(0);

  const resetGame = useCallback(() => {
    const newGame = new Chess();
    setGame(newGame);
    setMoveIndex(0);
    setMistakes(0);
    setStartTime(Date.now());
    setIsFirstAttempt(true);
    setHintsUsed(0);
    return newGame;
  }, []);

  const incrementMoveIndex = useCallback(() => {
    setMoveIndex(prev => prev + 1);
  }, []);

  const incrementMistakes = useCallback(() => {
    setMistakes(prev => prev + 1);
    setIsFirstAttempt(false);
  }, []);

  const incrementHints = useCallback(() => {
    setHintsUsed(prev => prev + 1);
  }, []);

  const reconstructGameToMove = useCallback((targetMoveIndex) => {
    if (!currentLine || targetMoveIndex < 0) return null;
    
    const newGame = new Chess();
    for (let i = 0; i < targetMoveIndex; i++) {
      try {
        newGame.move(currentLine.moves[i]);
      } catch (error) {
        console.error('Error reconstructing game:', error);
        return null;
      }
    }
    return newGame;
  }, [currentLine]);

  const isLineComplete = useCallback(() => {
    return currentLine && moveIndex >= currentLine.moves.length;
  }, [currentLine, moveIndex]);

  const isPlayerTurn = useCallback(() => {
    return currentLine && currentLine.playerMoves.includes(moveIndex);
  }, [currentLine, moveIndex]);

  return useMemo(() => ({
    // State
    game,
    moveIndex,
    currentLine,
    mistakes,
    startTime,
    isFirstAttempt,
    hintsUsed,
    
    // Setters
    setGame,
    setMoveIndex,
    setCurrentLine,
    
    // Actions
    resetGame,
    incrementMoveIndex,
    incrementMistakes,
    incrementHints,
    reconstructGameToMove,
    
    // Computed
    isLineComplete,
    isPlayerTurn
  }), [
    game, moveIndex, currentLine, mistakes, startTime, isFirstAttempt, hintsUsed,
    setGame, setMoveIndex, setCurrentLine,
    resetGame, incrementMoveIndex, incrementMistakes, incrementHints, reconstructGameToMove,
    isLineComplete, isPlayerTurn
  ]);
};